import { useId } from 'react';

import { cx } from '../lib/cx';

export interface ConfidenceMeterProps {
  /** Confidence 0–1. `null` renders a `—` for an unclassified document. */
  value: number | null;
  /** Auto-classification threshold; drives colour and the tick. Default 0.75. */
  threshold?: number;
  /** Track height: 8 (detail), 7 (reviewer) or 5 (row). */
  size?: 5 | 7 | 8;
  /** Show the threshold tick (detail/reviewer) — hidden on the tiny row meter. */
  showThreshold?: boolean;
  /** Accessible label prefix, e.g. "Classification confidence". */
  label?: string;
  className?: string;
}

/**
 * Labelled confidence meter — a `role="meter"` track with the numeric value
 * ALWAYS rendered in mono alongside (never colour alone; design-reference §6.2).
 * Colour follows acceptability: ≥ threshold → --ok, below → --warn-dot.
 */
export function ConfidenceMeter({
  value,
  threshold = 0.75,
  size = 8,
  showThreshold = true,
  label = 'Confidence',
  className,
}: ConfidenceMeterProps): React.JSX.Element {
  const id = useId();
  if (value === null) {
    return (
      <span className={cx('ds-meter', className)}>
        <span className="ds-meter__value" style={{ color: 'var(--t3)' }}>
          —
        </span>
      </span>
    );
  }
  const pct = Math.round(value * 100);
  const colour = value >= threshold ? 'var(--ok)' : 'var(--warn-dot)';
  const text = value >= threshold ? 'var(--ok-ink)' : 'var(--warn)';
  const markHeight = size === 8 ? 14 : 13;
  return (
    <span className={cx('ds-meter', className)}>
      <span
        className="ds-meter__track"
        style={{ height: `${String(size)}px` }}
        role="meter"
        aria-labelledby={id}
        aria-valuemin={0}
        aria-valuemax={1}
        aria-valuenow={value}
        aria-valuetext={`${value.toFixed(2)} confidence, threshold ${threshold.toFixed(2)}`}
      >
        <span className="ds-meter__fill" style={{ width: `${String(pct)}%`, background: colour }} />
        {showThreshold ? (
          <span
            className="ds-meter__mark"
            style={{ left: `${String(threshold * 100)}%`, height: `${String(markHeight)}px` }}
          />
        ) : null}
      </span>
      <span id={id} className="ds-meter__value" style={{ color: text }}>
        {value.toFixed(2)}
        <span className="sr-only"> {label}</span>
      </span>
    </span>
  );
}
